import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const MainHome: React.FC = () => {
  const heroContent = {
    title: 'Gyaanly: Where Knowledge Meets Opportunity',
    subtitle: 'Comprehensive platform designed to empower creators to share their expertise, reach a global audience, and build sustainable income streams.',
    description: 'Whether you’re an industry expert, educator, or content creator, Gyaanly gives you the tools, community, page-building capabilities, and infrastructure to turn your knowledge into a thriving business.',
    subDescription: 'At the same time, Gyaanly connects you with motivated learners who are eager to gain new skills and insights directly from creators like you — making education more personal, impactful, and transformative.',
    cta: 'Start Your Journey',
  };

  const forLearners = {
    title: 'For Learners',
    description: 'Discover new skills, advance your career, and achieve your learning goals with expert-led courses and personalized guidance.',
    benefits: [
      { icon: '📚', title: 'Expert-Led Courses', description: 'Learn from certified professionals and industry experts who have proven track records in their fields.' },
      { icon: '🔄', title: 'Study at your own pace', description: 'Study at your own pace with access to course materials and updates as and when required.' },
      { icon: '📱', title: 'Multi-Platform', description: 'Access your courses on any device, anywhere, anytime for maximum flexibility.' },
      { icon: '🏆', title: 'Certification', description: 'Earn certificates upon completion to showcase your new skills and boost your resume.' },
      { icon: '👥', title: '1:1 Sessions', description: 'Book personalized consultations with creators for direct guidance and mentorship.' },
      { icon: '💡', title: 'Practical Skills', description: 'Focus on real-world applications and practical knowledge that you can use immediately.' }
    ],
    cta: 'Explore Courses',
    ctaLink: '/learn'
  };

  const forCreators = {
    title: 'For Creators',
    description: 'Transform your expertise into a scalable business with us. Whether you’re an industry expert, educator, or content creator, Gyaanly gives you the tools, community, page-building capabilities, and infrastructure to turn your knowledge into a thriving business.',
    benefits: [
      { icon: '💰', title: 'Passive Income', description: 'Create once, earn forever. Build multiple income streams through course sales, memberships, and affiliate programs.' },
      { icon: '🌍', title: 'Global Reach', description: 'Teach learners worldwide. Our platform reaches millions of eager students looking for your expertise.' },
      { icon: '⚡', title: 'Scalable Business', description: 'Scale your teaching business without limits. Automate your workflow and focus on what you love.' },
      { icon: '📄', title: 'Custom Branded Pages', description: 'Build and host your own branded pages with our simple drag-and-drop page builder. Create landing pages, portfolios, and more.' },
      { icon: '📞', title: '1:1 Consultations', description: 'Offer personalized advice, coaching, and Q&A sessions directly to your most dedicated learners.' },
      { icon: '🔄', title: 'Automated Workflows', description: 'Streamline registrations, payments, and scheduling to focus on content creation and growth.' }
    ],
    cta: 'Start Creating',
    ctaLink: '/animated-creator'
  };

  const stats = [
    { number: '50K+', label: 'Active Learners' },
    { number: '500+', label: 'Expert Courses' },
    { number: '200+', label: 'Knowledge Creators' },
    { number: '95%', label: 'Satisfaction Rate' },
    { number: '$15K+', label: 'Top Creator Earnings' },
    { number: '24/7', label: 'Platform Availability' }
  ];

  const workflowSteps = [
    { step: '01', title: 'Choose Your Path', description: 'Decide whether you want to learn new skills or share your expertise as a creator.', icon: '🛤️' },
    { step: '02', title: 'Get Started', description: 'Sign up and explore our platform features designed specifically for your needs.', icon: '🚀' },
    { step: '03', title: 'Engage & Grow', description: 'Start learning, creating, or both. Our community and tools support your journey.', icon: '📈' },
    { step: '04', title: 'Achieve Success', description: 'Reach your goals whether it\'s mastering new skills or building a thriving creator business.', icon: '🎯' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-primary-50">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 via-transparent to-accent-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-display font-bold text-gray-900 mb-6 leading-tight">
              {heroContent.title}
            </h1>
            <p className="text-3xl md:text-2xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
              {heroContent.subtitle}
            </p>
            
          </motion.div>
        </div>
      </section>


       {/* For Creators Section */}
       <section className="py-10 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              {forCreators.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {forCreators.description}
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {forCreators.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="card-hover p-6 text-center group"
              >
                <div className="w-16 h-16 icon-primary flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">{benefit.icon}</span>
                </div>
                <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center"
          >
            <Link to={forCreators.ctaLink} className="btn-primary text-lg px-8 py-4 inline-flex items-center">
              <span className="material-symbols-rounded mr-2">add_circle</span>
              {forCreators.cta}
            </Link>
          </motion.div> */}
        </div>
      </section>

      {/* For Learners Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              {forLearners.title}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {forLearners.description}
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {forLearners.benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="card-hover p-6 text-center group"
              >
                <div className="w-16 h-16 icon-primary flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <span className="text-3xl">{benefit.icon}</span>
                </div>
                <h3 className="text-xl font-display font-semibold text-gray-900 mb-3">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>


      {/* How It Works Section */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.2 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting started with Gyaanly is simple and straightforward. Choose your path and begin your journey.
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {workflowSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true, amount: 0.2 }}
                className="relative"
              >
                <div className="card-hover p-8 text-center h-full">
                  <div className="text-4xl mb-4">{step.icon}</div>
                  <div className="text-sm font-bold text-primary-600 mb-2">{step.step}</div>
                  <h3 className="text-xl font-display font-semibold text-gray-900 mb-4">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
                {index < workflowSteps.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <div className="w-8 h-0.5 bg-primary-400"></div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 gradient-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, amount: 0.2 }}
          >
            <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-6">
              Ready to Transform Your Future?
            </h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Whether you want to learn new skills or share your expertise, Gyaanly provides the platform, tools, and community you need to succeed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/early-access" className="glass-button text-lg px-8 py-4">
                <span className="material-symbols-rounded mr-2">rocket_launch</span>
                Register for Early Access
              </Link>
              
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MainHome;
